import { IconPlayerPause, IconBell, IconAlertCircle, IconShieldCheck } from "@tabler/icons-react";

import { cn } from "@/lib/utils";
import type { MoneyOrConflict, RoasBucket, RuleOutcome } from "@/services/metaPerformance.service";

import { BUCKET, fmtMoney, mocParts } from "./format";

/** ROAS bucket as a dot and a word, never a pill; the tone is the status colour. */
export function BucketText({ bucket, roas }: { bucket: RoasBucket; roas?: string }) {
  const b = BUCKET[bucket];
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap" title={b.hint}>
      <span aria-hidden className={cn("size-1.5 shrink-0 rounded-full", b.dot)} />
      {roas && <span className="tabular-nums text-foreground">{roas}</span>}
      <span className={cn("text-xs", b.text)}>{b.label}</span>
    </span>
  );
}

/** One currency as a figure; several as "Mixed" with the parts underneath. */
export function MixedMoney({ m, fallbackCurrency = "" }: { m: MoneyOrConflict; fallbackCurrency?: string }) {
  if (!m.conflict) return <>{fmtMoney(m.amount, m.currency || fallbackCurrency)}</>;
  return (
    <span className="inline-flex flex-col leading-tight" title="These orders were placed in different currencies; they are not added together">
      <span className="text-amber-700 dark:text-amber-400">Mixed</span>
      <span className="text-[11px] font-normal text-muted-foreground">{mocParts(m).join(" · ")}</span>
    </span>
  );
}

const OUTCOME: Record<RuleOutcome, { label: string; icon: typeof IconBell; cls: string }> = {
  NOTIFIED: { label: "Notified", icon: IconBell, cls: "text-muted-foreground" },
  PAUSED: { label: "Paused", icon: IconPlayerPause, cls: "text-red-600 dark:text-red-400" },
  PAUSE_FAILED: { label: "Pause failed", icon: IconAlertCircle, cls: "text-amber-700 dark:text-amber-400" },
};

export function EventOutcome({ outcome }: { outcome: RuleOutcome }) {
  const o = OUTCOME[outcome];
  const Icon = o.icon;
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1 text-xs font-medium", o.cls)}>
      <Icon size={14} stroke={1.75} aria-hidden />
      {o.label}
    </span>
  );
}

/** A tiny bar sparkline for weekly revenue; bars share one scale. */
export function Sparkline({ values, className }: { values: number[]; className?: string }) {
  const max = Math.max(0, ...values);
  return (
    <span className={cn("inline-flex h-5 items-end gap-px", className)} aria-hidden>
      {values.map((v, i) => (
        <span key={i} className="w-1.5 rounded-[1px] bg-brand-600/70" style={{ height: `${max > 0 ? Math.max(8, (v / max) * 100) : 8}%`, opacity: v > 0 ? 1 : 0.35 }} />
      ))}
    </span>
  );
}

/** Reveshare-launched marker: a small mark, not a pill. */
export function LaunchedMark({ on }: { on: boolean }) {
  if (!on) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-600" title="Launched from Reveshare">
      <IconShieldCheck size={13} stroke={1.75} aria-hidden />
      Reveshare
    </span>
  );
}
